import { Avatar, Flex, Tooltip } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';

import { User, useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { useUserColor } from '~/packages/comments/useUserColor';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';

// Types
type PartialUser = Pick<User, 'id' | 'firstName' | 'lastName'> & {
  organization: Pick<User['organization'], 'id' | 'name'>;
};

// Exports
type UserTooltipProps = { children: React.ReactNode; user: PartialUser };
export const UserTooltip = ({ children, user }: UserTooltipProps) => {
  const { user: loggedInUser } = useUserAuthOrThrow();
  return (
    <Tooltip
      openDelay={300}
      as="span"
      alignItems="flex-start"
      display="inline-flex"
      flexDirection="column"
      textAlign="center"
      label={
        <>
          <Flex as="span" alignItems="baseline" gap="0.5rem">
            <Icon size="xs" icon="user" style={{ flexShrink: 0 }} />{' '}
            <Flex as="span" alignItems="baseline" flexGrow={1}>
              {user.firstName} {user.lastName}{' '}
              {user.id === loggedInUser.id && '(you)'}
            </Flex>
          </Flex>
          <Flex as="span" alignItems="baseline" gap="0.5rem">
            <Icon size="xs" icon="building" style={{ flexShrink: 0 }} />{' '}
            <Flex as="span" alignItems="baseline" flexGrow={1}>
              {user.organization.name}{' '}
              {user.organization.id === loggedInUser.organization.id
                ? '(your org)'
                : '(external org)'}
            </Flex>
          </Flex>
        </>
      }
    >
      {children}
    </Tooltip>
  );
};

type UserDetailProps = {
  variant: 'avatar' | 'name' | 'avatar-name';
  user: PartialUser;
};
/**
 * Variants:
 * - `avatar`: Show just the user avatar. Tooltip contains user details.
 * - `name`: Show just the user name. Tooltip contains user details.
 * - `avatar-name`: Show the avatar and name next to each other. This is similar to "@-mentions" UI
 *    in apps like Slack and Github. Tooltip contains user details.
 */
export const UserDetail = ({
  variant = 'avatar-name',
  user,
}: UserDetailProps) => {
  const { user: loggedInUser } = useUserAuthOrThrow();
  const avatarBackgroundColor = useUserColor(user.id);
  const content = useMemo(() => {
    const firstName = _.upperFirst(user.firstName);
    const lastName = _.upperFirst(user.lastName);

    const avatar = (
      <Avatar
        size="xs"
        background={avatarBackgroundColor}
        flexShrink={0}
        name={`${firstName} ${lastName}`}
        icon={<Icon icon="user" />}
        zIndex="1"
        userSelect="none"
        color="black"
      />
    );

    const name = (
      <Typography
        inline
        variant="detail"
        fontWeight="semibold"
        TextProps={{ userSelect: 'none' }}
      >
        {firstName} {lastName}
        {user.organization.id !== loggedInUser.organization.id && (
          <span>
            {' '}
            <Icon size="xs" icon="share-nodes" />
          </span>
        )}
      </Typography>
    );

    if (variant === 'avatar') return avatar;
    if (variant === 'name') return name;
    return (
      <span>
        {avatar} {name}
      </span>
    );
  }, [
    avatarBackgroundColor,
    loggedInUser.organization.id,
    user.firstName,
    user.lastName,
    user.organization.id,
    variant,
  ]);

  return (
    <UserTooltip user={user}>
      <span>{content}</span>
    </UserTooltip>
  );
};
